import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { GraduationCap, Heart, Lightbulb, Users, CheckCircle2 } from "lucide-react";

function Programs() {
  const programs = [
    {
      title: "CDB S.T.E.A.M. Program",
      icon: <Lightbulb className="w-12 h-12" />,
      tagline: "Technology & Engineering Education",
      description: "In this exciting program, students will learn communications, engineering, entrepreneurship, financial literacy, and cutting-edge real-world technologies up close and in person. Gaining tangible skills exploring how STEAM components are used to solve real-world problems and create career opportunities across various industries.",
      image: "/images/gallery/IMG_1366.jpg",
      features: [
        "Coding & Software Design",
        "3D Printing & Modeling",
        "Micro bits & Breadboards",
        "Ultrasonic Sensors",
        "Digital Currency & Finance",
        "Real-World Engineering Projects",
        "Field Trips to Tech Companies",
        "Guest Speakers from Industry"
      ],
      facilitators: "Compudopt trainers, educators, college students, and professional community guests",
      outcomes: [
        "Hands-on technical skills",
        "Career exploration in STEAM fields",
        "Real-world problem-solving abilities",
        "Industry connections and mentorship"
      ]
    },
    {
      title: "THOROpy Program",
      icon: <Heart className="w-12 h-12" />,
      tagline: "Mental Health & Personal Development",
      description: "The THOROpy program is designed to empower children, build their self-confidence, enhance their beauty and style, and improve their mental health and social skills through interactive learning and fun activities.",
      image: "/images/gallery/DSC03789_VSCO.jpeg",
      features: [
        "Self-Confidence Building",
        "Mental Health Support",
        "Social Skills Development",
        "Beauty & Personal Style",
        "Art & Music Therapy",
        "Creative Expression Workshops",
        "Peer Support Groups",
        "Wellness Activities"
      ],
      outcomes: [
        "Increased self-esteem and confidence",
        "Better mental health awareness",
        "Improved social interactions",
        "Creative outlets for expression"
      ]
    },
    {
      title: "The Bridge Program",
      icon: <GraduationCap className="w-12 h-12" />,
      tagline: "Trade Skills & Job Placement",
      description: "The Bridge Program is designed to offer services and resources to students who wish to learn a trade following high school graduation. Through this program, we provide graduates with scholarships for any trade that requires courses and certification fees. Once students have completed the required course, they will have immediate job placement within our team's umbrella of trades for at least one year.",
      image: "/images/gallery/IMG_1470.jpg",
      features: [
        "Full Scholarship Coverage",
        "Carpentry Training",
        "Truck Driving Certification",
        "Fashion & Design",
        "Culinary Arts",
        "Customer Service & Retail",
        "Esthetics & Beauty",
        "Guaranteed Job Placement (1 year minimum)"
      ],
      outcomes: [
        "Industry-recognized certifications",
        "Immediate employment opportunities",
        "Living wage career paths",
        "Long-term professional mentorship"
      ]
    },
    {
      title: "Mental Health & Creative Wellness",
      icon: <Heart className="w-12 h-12" />,
      tagline: "Therapeutic Arts & Wellness",
      description: "Art therapy and music therapy programming creating safe spaces for emotional growth and self-awareness. Includes our signature 'Meet at the Mountain' wellness hiking program.",
      image: "/images/gallery/IMG_1804.JPG",
      features: [
        "Art Therapy Sessions",
        "Music Therapy Programming",
        "Meet at the Mountain Wellness Hikes",
        "Mindfulness & Meditation",
        "Confidence Building Activities",
        "Emotional Intelligence Training",
        "Safe Space Discussion Groups",
        "Mental Health Awareness Campaigns"
      ],
      outcomes: [
        "Emotional regulation skills",
        "Stress management techniques",
        "Community connection and support",
        "Improved overall well-being"
      ]
    }
  ];

  const additionalServices = [
    {
      category: "STEAM & Leadership Development",
      items: [
        "Hands-on STEAM instruction",
        "Leadership identity building",
        "Career exploration and workforce readiness",
        "Partnerships with schools and community organizations"
      ]
    },
    {
      category: "Second Chance & Youth Empowerment",
      items: [
        "Support for youth involved in or impacted by the juvenile justice system",
        "Exposure to career pathways including esports and digital industries",
        "Social-emotional learning and mentorship"
      ]
    },
    {
      category: "Community Engagement & Outreach",
      items: [
        "Community workshops and events",
        "Scholarship support",
        "Mental health awareness initiatives",
        "Family engagement programs"
      ]
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Header */}
      <section className="pt-32 pb-16 px-4 bg-gradient-to-br from-[#1167B1] to-[#0d5390] text-white">
        <div className="max-w-7xl mx-auto text-center">
          <Badge className="bg-[#FFE102] text-[#17181B] font-bold mb-6">Our Programs</Badge>
          <h1 className="text-5xl md:text-6xl font-bold mb-6">Comprehensive Youth Development Programs</h1>
          <p className="text-xl text-white/90 max-w-4xl mx-auto">
            From STEAM education to mental wellness, our programs are designed to empower youth with the skills, confidence, and support they need to build successful futures.
          </p>
        </div>
      </section>

      {/* Main Programs */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-7xl mx-auto space-y-20">
          {programs.map((program, index) => (
            <div key={index} className={`grid md:grid-cols-2 gap-12 items-start ${index % 2 === 1 ? 'md:flex-row-reverse' : ''}`}>
              <div className={index % 2 === 1 ? 'md:order-2' : ''}>
                <Card className="border-2 border-[#1167B1] h-full">
                  <div className="h-64 overflow-hidden">
                    <img src={program.image} alt={program.title} className="w-full h-full object-cover" />
                  </div>
                  <CardHeader>
                    <div className="w-20 h-20 bg-[#1167B1] text-white rounded-full flex items-center justify-center mb-4">
                      {program.icon}
                    </div>
                    <CardTitle className="text-3xl text-[#17181B]">{program.title}</CardTitle>
                    <CardDescription className="text-lg font-semibold text-[#1167B1]">{program.tagline}</CardDescription>
                  </CardHeader>
                </Card>
              </div>
              
              <div className={index % 2 === 1 ? 'md:order-1' : ''}>
                <div className="space-y-6">
                  <p className="text-lg text-gray-700 leading-relaxed">{program.description}</p>
                  
                  <div>
                    <h3 className="text-2xl font-bold text-[#17181B] mb-4">Program Features</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {program.features.map((feature, idx) => (
                        <div key={idx} className="flex items-start space-x-2">
                          <CheckCircle2 className="w-5 h-5 text-[#FFE102] flex-shrink-0 mt-1" />
                          <span className="text-gray-700">{feature}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {program.facilitators && (
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <p className="text-sm text-gray-600"><span className="font-semibold">Facilitated by:</span> {program.facilitators}</p>
                    </div>
                  )}

                  <div>
                    <h4 className="font-bold text-lg text-[#17181B] mb-3">Student Outcomes</h4>
                    <ul className="space-y-2">
                      {program.outcomes.map((outcome, idx) => (
                        <li key={idx} className="flex items-start space-x-2">
                          <div className="w-2 h-2 bg-[#1167B1] rounded-full mt-2"></div>
                          <span className="text-gray-700">{outcome}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Additional Services */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <Badge className="bg-[#1167B1] text-white mb-4">Additional Services</Badge>
            <h2 className="text-4xl font-bold text-[#17181B] mb-4">Comprehensive Support Services</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">Beyond our core programs, we offer a wide range of support services to meet the diverse needs of our youth and families.</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {additionalServices.map((service, index) => (
              <Card key={index} className="border-2 hover:border-[#1167B1] transition-all">
                <CardHeader>
                  <CardTitle className="text-xl text-[#17181B]">{service.category}</CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-3">
                    {service.items.map((item, idx) => (
                      <li key={idx} className="flex items-start space-x-2">
                        <CheckCircle2 className="w-5 h-5 text-[#1167B1] flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700 text-sm">{item}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Community Impact */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-5xl mx-auto">
          <Card className="border-2 border-[#1167B1] shadow-xl">
            <CardHeader className="text-center">
              <Users className="w-12 h-12 mx-auto mb-4 text-[#1167B1]" />
              <CardTitle className="text-3xl text-[#17181B]">Community Reach & Impact</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <h3 className="font-bold text-xl text-[#17181B] mb-3">Service Area</h3>
                <p className="text-gray-700">Programs have served youth and families across Metro Atlanta, including the MLK (Adamsville) community, through school partnerships, community-based programming, and collaborative initiatives.</p>
              </div>
              
              <div>
                <h3 className="font-bold text-xl text-[#17181B] mb-3">Impact Outcomes</h3>
                <ul className="grid md:grid-cols-2 gap-3">
                  <li className="flex items-start space-x-2">
                    <CheckCircle2 className="w-5 h-5 text-[#FFE102] flex-shrink-0 mt-1" />
                    <span className="text-gray-700">Increased student confidence and engagement</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <CheckCircle2 className="w-5 h-5 text-[#FFE102] flex-shrink-0 mt-1" />
                    <span className="text-gray-700">Improved leadership and communication skills</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <CheckCircle2 className="w-5 h-5 text-[#FFE102] flex-shrink-0 mt-1" />
                    <span className="text-gray-700">Greater access to mental health-centered programming</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <CheckCircle2 className="w-5 h-5 text-[#FFE102] flex-shrink-0 mt-1" />
                    <span className="text-gray-700">Strengthened family and community connections</span>
                  </li>
                </ul>
              </div>

              <div>
                <h3 className="font-bold text-xl text-[#17181B] mb-3">Looking Ahead</h3>
                <p className="text-gray-700 mb-4">Calvin D. Butts Inc. continues to expand its reach through:</p>
                <ul className="space-y-2">
                  <li className="flex items-start space-x-2">
                    <div className="w-2 h-2 bg-[#1167B1] rounded-full mt-2"></div>
                    <span className="text-gray-700">Sustainable partnerships</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <div className="w-2 h-2 bg-[#1167B1] rounded-full mt-2"></div>
                    <span className="text-gray-700">Scalable youth programming</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <div className="w-2 h-2 bg-[#1167B1] rounded-full mt-2"></div>
                    <span className="text-gray-700">Expanded STEAM and leadership initiatives</span>
                  </li>
                </ul>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-4 bg-[#1167B1]">
        <div className="max-w-4xl mx-auto text-center text-white">
          <h2 className="text-4xl font-bold mb-6">Ready to Get Started?</h2>
          <p className="text-xl mb-8 text-white/90">Enroll your child in one of our life-changing programs today.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button className="bg-[#FFE102] text-[#17181B] hover:bg-[#FFE102]/90 font-bold text-lg px-8 py-6 rounded-full">
                Enroll Now
              </Button>
            </Link>
            <Link to="/donate">
              <Button variant="outline" className="bg-white text-[#1167B1] hover:bg-white/90 font-bold text-lg px-8 py-6 rounded-full border-2">
                Support Our Programs
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

export default Programs;